import type { FormEvent } from "react";
import { MARKET_CONDITIONS, MARKET_LANGUAGES, MARKET_PAYMENT_METHODS, PAYMENT_LABEL, type MarketFilters } from "../../shared/market";
import { RATED_ZONES, ZONE_LABEL } from "../../shared/shipping";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { gameFinishes } from "../../shared/games";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { useGame } from "../lib/GameProvider";

/**
 * The listing filters' form: the market's sidebar, and a shop's. In a shop (`shop`) the shop is given,
 * so the seller and their reputation, zone and payments aren't asked, and the search is above the results.
 * `onApply` gets only the filters this form asks.
 */
export function MarketFiltersForm({ filters, shop = false, onApply, onClear }: { filters: MarketFilters; shop?: boolean; onApply: (patch: Partial<MarketFilters>) => void; onClear: () => void }) {
  const { t, locale, pick } = useI18n();
  const { categories } = useCatalog().catalog;
  const { game } = useGame();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? "").trim();
    const num = (k: string) => (text(k) === "" ? null : Number(text(k)));
    const card: Partial<MarketFilters> = {
      categories: text("category") ? [text("category")] : [],
      conditions: f.getAll("condition").map(String) as MarketFilters["conditions"],
      languages: text("language") ? [text("language")] : [],
      finish: (text("finish") || null) as MarketFilters["finish"],
      graded: text("graded") === "" ? null : text("graded") === "1",
      minPrice: num("min"),
      maxPrice: num("max"),
    };
    onApply(
      shop
        ? card
        : {
            ...card,
            q: text("q"),
            seller: text("seller").replace(/^@/, "").toLowerCase() || null,
            zone: (text("zone") || null) as MarketFilters["zone"],
            payment: (text("payment") || null) as MarketFilters["payment"],
            minRating: num("rating"),
            minSales: num("sales"),
          },
    );
  };

  return (
    <form className="mkt-filters stack" onSubmit={onSubmit}>
      {!shop && (
        <>
          <div className="field">
            <label htmlFor="mf-q">{t("mkt.f.search")}</label>
            <input id="mf-q" name="q" type="search" defaultValue={filters.q} placeholder={t("mkt.f.searchHint")} />
          </div>
          <div className="field">
            <label htmlFor="mf-seller">{t("mkt.f.seller")}</label>
            <input id="mf-seller" name="seller" defaultValue={filters.seller ?? ""} placeholder="@usuario" autoCapitalize="none" />
          </div>
        </>
      )}
      <div className="field">
        <label htmlFor="mf-category">{t("mkt.f.category")}</label>
        <select id="mf-category" name="category" defaultValue={filters.categories[0] ?? ""}>
          <option value="">{t("mkt.f.any")}</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {pick(c.name)}
            </option>
          ))}
        </select>
      </div>
      <fieldset className="check-list">
        <legend>{t("mkt.f.condition")}</legend>
        {MARKET_CONDITIONS.map((c) => (
          <label key={c}>
            <input type="checkbox" name="condition" value={c} defaultChecked={filters.conditions.includes(c)} /> {CONDITION_LABEL[c][locale]}
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor="mf-lang">{t("mkt.f.language")}</label>
        <select id="mf-lang" name="language" defaultValue={filters.languages[0] ?? ""}>
          <option value="">{t("mkt.f.any")}</option>
          {MARKET_LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {LANGUAGE_LABEL[l]?.[locale] ?? l}
            </option>
          ))}
        </select>
      </div>
      {gameFinishes(game).length > 1 && (
        <div className="field">
          <label htmlFor="mf-finish">{t("mkt.f.finish")}</label>
          <select id="mf-finish" name="finish" defaultValue={filters.finish ?? ""}>
            <option value="">{t("mkt.f.any")}</option>
            {gameFinishes(game).map((f) => (
              <option key={f} value={f}>
                {FINISH_LABEL[f][locale]}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label htmlFor="mf-graded">{t("mkt.f.graded")}</label>
        <select id="mf-graded" name="graded" defaultValue={filters.graded === null ? "" : filters.graded ? "1" : "0"}>
          <option value="">{t("mkt.f.any")}</option>
          <option value="1">{t("mkt.f.gradedYes")}</option>
          <option value="0">{t("mkt.f.gradedNo")}</option>
        </select>
      </div>
      <div className="price-inputs">
        <div className="field">
          <label htmlFor="mf-min">{t("mkt.f.min")}</label>
          <input id="mf-min" name="min" type="number" min={0} inputMode="numeric" defaultValue={filters.minPrice ?? ""} />
        </div>
        <div className="field">
          <label htmlFor="mf-max">{t("mkt.f.max")}</label>
          <input id="mf-max" name="max" type="number" min={0} inputMode="numeric" defaultValue={filters.maxPrice ?? ""} />
        </div>
      </div>
      {!shop && (
        <>
          <div className="field">
            <label htmlFor="mf-zone">{t("mkt.f.zone")}</label>
            <select id="mf-zone" name="zone" defaultValue={filters.zone ?? ""}>
              <option value="">{t("mkt.f.any")}</option>
              {RATED_ZONES.map((z) => (
                <option key={z} value={z}>
                  {ZONE_LABEL[z][locale]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mf-pay">{t("mkt.f.payment")}</label>
            <select id="mf-pay" name="payment" defaultValue={filters.payment ?? ""}>
              <option value="">{t("mkt.f.any")}</option>
              {MARKET_PAYMENT_METHODS.map((p) => (
                <option key={p} value={p}>
                  {PAYMENT_LABEL[p][locale]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mf-rating">{t("mkt.f.rating")}</label>
            <select id="mf-rating" name="rating" defaultValue={filters.minRating ?? ""}>
              <option value="">{t("mkt.f.any")}</option>
              {[4.5, 4, 3].map((r) => (
                <option key={r} value={r}>
                  {t("mkt.f.ratingAtLeast", { value: r.toLocaleString(locale === "es" ? "es-AR" : "en-US") })}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="mf-sales">{t("mkt.f.sales")}</label>
            <select id="mf-sales" name="sales" defaultValue={filters.minSales ?? ""}>
              <option value="">{t("mkt.f.any")}</option>
              {[1, 5, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {t("mkt.f.salesAtLeast", { count: n })}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <button type="submit" className="btn btn-primary btn-block">
        {t("catalog.applyFilters")}
      </button>
      <button type="button" className="btn btn-link" onClick={onClear}>
        {t("catalog.clearFilters")}
      </button>
    </form>
  );
}
